<template>
  <!-- Figma 1:13536 › ກວດສອບຍອດຄ້າງຊຳລະ (2168:4334). The design's
       ຍອດຄ່າທຳນຽມຄ້າງຊຳລະ column is left out on purpose: the fee comes off the
       payout, so no fee is ever owed afterwards (`fee_outstanding` is always 0). -->
  <div class="audit-tab">
    <div class="list-head">
      <h2 class="list-title">
        <i class="fal fa-file-invoice-dollar"></i>
        ລາຍການຮ້ານຄ້າຄ້າງຊຳລະຖອນ COD ລ່ວງໜ້າ
        <span class="list-count">({{ pagination.total }})</span>
      </h2>
      <p class="list-total">ຍອດຄ້າງຊຳລະທັງໝົດ : {{ formatKip(sumOutstanding) }}</p>
      <button type="button" class="cod-btn is-pale list-head-action" :disabled="exporting" @click="exportRows">
        <i class="fal fa-file-excel"></i> {{ exporting ? 'ກຳລັງສົ່ງອອກ...' : 'Export' }}
      </button>
    </div>

    <div class="filter-card">
      <div class="filter-field">
        <label for="cod-audit-search">ຄົ້ນຫາ:</label>
        <input id="cod-audit-search" class="filter-input" type="text" v-model="filters.search"
               placeholder="ຊື່ຮ້ານ / ເບີໂທ" @keyup.enter="fetchData(1)">
      </div>
      <div class="filter-field">
        <label for="cod-audit-shop-status">ສະຖານະຮ້ານ:</label>
        <select id="cod-audit-shop-status" class="filter-input" v-model="filters.shop_status" @change="fetchData(1)">
          <option value="all">ທັງໝົດ</option>
          <option value="enabled">ເປີດນຳໃຊ້</option>
          <option value="disabled">ປິດນຳໃຊ້</option>
        </select>
      </div>
      <button class="round-btn" type="button" aria-label="ຄົ້ນຫາ" @click="fetchData(1)">
        <i class="fal fa-search"></i>
      </button>
    </div>

    <div class="table-card">
      <vs-table :data="rows">
        <template #thead>
          <vs-tr>
            <vs-th>ລຳດັບ</vs-th>
            <vs-th>ຊື່ຮ້ານ</vs-th>
            <vs-th>ເບີໂທ</vs-th>
            <vs-th>ຍອດຖອນສູງສຸດ</vs-th>
            <vs-th>ຍອດຖອນປັດຈຸບັນ</vs-th>
            <vs-th>ຍອດຄ້າງຊຳລະ</vs-th>
            <vs-th>% ໃນການຖອນ</vs-th>
            <vs-th>ຄ່າທຳນຽມ</vs-th>
            <vs-th>ສະຖານະ</vs-th>
            <vs-th>ວັນທີຖອນ</vs-th>
            <vs-th></vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr :key="row.customer.id" v-for="(row, index) in rows">
            <vs-td>{{ rowNumber(index) }}</vs-td>
            <vs-td>{{ row.shop.name }}</vs-td>
            <vs-td>{{ row.customer.tel || '—' }}</vs-td>
            <vs-td>{{ formatKip(row.cap_amount) }}</vs-td>
            <!-- drawn = outstanding debt + the advance still reserved for a payout. -->
            <vs-td>{{ formatKip(row.drawn) }}</vs-td>
            <vs-td>
              <span :class="{'text-bad': row.outstanding > 0}">
                {{ row.outstanding > 0 ? '-' : '' }}{{ formatKip(row.outstanding) }}
              </span>
            </vs-td>
            <!-- The percent and its amount (owner, 2026-10-07): the COD the
                 advance percent applies to (its percent is ຍອດຖອນສູງສຸດ), and
                 the fees on what is drawn now (taken / waiting for Finance). -->
            <vs-td>
              {{ formatPct(row.advance_rate_pct) }}
              <span class="cell-note" v-if="row.base_effective > 0">ຈາກ {{ formatKip(row.base_effective) }}</span>
            </vs-td>
            <vs-td>
              {{ formatPct(row.fee_pct) }}
              <span class="cell-note" v-if="row.fees_taken > 0">ເກັບແລ້ວ {{ formatKip(row.fees_taken) }}</span>
              <!-- On advances waiting for Finance: taken only if the transfer is made. -->
              <span class="cell-note" v-if="row.fees_pending > 0">ຈະຫັກຕອນໂອນ {{ formatKip(row.fees_pending) }}</span>
            </vs-td>
            <!-- `status` arrives already in Lao from the server; do not relabel it. -->
            <vs-td>
              <span :class="row.outstanding > 0 ? 'text-bad' : 'text-ok'">{{ row.status }}</span>
            </vs-td>
            <!-- When the shop ASKED for its oldest open advance. The transfer
                 time (`oldest_open_advance_at`) is only the fallback for an
                 API that predates the field; it still drives overdue. -->
            <vs-td>{{ withdrawDate(row) ? formatDateTime(withdrawDate(row)) : '—' }}</vs-td>
            <vs-td>
              <!-- An icon like the waiting tab's ✕ / ↺, named on hover: this
                   table already scrolls sideways, and a word would push the
                   one destructive action further out of view. -->
              <div class="row-actions">
                <!-- Money the shop paid back itself, with a slip — not a write-off. -->
                <button v-if="row.outstanding > 0 && canWriteOff" type="button"
                        class="icon-btn" title="ຮັບຊຳລະໜີ້" aria-label="ຮັບຊຳລະໜີ້"
                        @click="openRepayment(row)">
                  <i class="fal fa-hand-holding-usd"></i>
                </button>
                <button v-if="row.outstanding > 0 && canWriteOff" type="button"
                        class="icon-btn is-danger" title="ຕັດໜີ້" aria-label="ຕັດໜີ້"
                        @click="openWriteOff(row)">
                  <i class="fal fa-eraser"></i>
                </button>
              </div>
            </vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <Pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="10" @paginate="fetchData"/>
        </template>
      </vs-table>

      <p class="list-state is-loading" v-if="isLoading">ກຳລັງໂຫລດ...</p>
      <p class="list-state" v-else-if="!rows.length">ບໍ່ມີລາຍການ</p>
      <p class="list-state is-error" v-if="error">{{ error }}</p>
    </div>

    <vs-dialog prevent-close :not-close="repaymentBusy" v-model="repaymentActive" v-if="repaymentActive" width="500px">
      <template #header>
        <h4 class="dialog-title">ຮັບຊຳລະໜີ້</h4>
      </template>
      <RepaymentModal :row="repaymentRow" @close="repaymentActive = false" @done="afterRepayment"
                      @busy="repaymentBusy = $event"/>
    </vs-dialog>

    <vs-dialog prevent-close v-model="writeOffActive" v-if="writeOffActive" width="480px">
      <template #header>
        <h4 class="dialog-title">ຕັດໜີ້</h4>
      </template>
      <div class="write-off">
        <div class="who">
          <span class="avatar is-lg">{{ initialOf(writeOffRow.shop.name) }}</span>
          <span class="person-text">
            <b class="person-name">{{ writeOffRow.shop.name }}</b>
            <span class="person-tel">{{ writeOffRow.customer.name }} · {{ writeOffRow.customer.tel }}</span>
          </span>
          <span class="who-owing">
            <span>ຄ້າງຊຳລະ</span>
            <b>{{ formatKip(writeOffRow.outstanding) }}</b>
          </span>
        </div>

        <p class="danger-note">
          <i class="fal fa-exclamation-triangle"></i>
          ການຕັດໜີ້ຈະຫຼຸດຍອດຄ້າງຊຳລະໂດຍບໍ່ມີການຮັບເງິນຈິງ ແລະ ຍົກເລີກຄືນບໍ່ໄດ້
        </p>

        <!-- A write-off targets ONE advance, and no other screen shows an
             advance id — so the open advances are listed to pick from. -->
        <div class="fld">
          <label class="fld-label" for="cod-write-off-advance">ລາຍການຖອນລ່ວງໜ້າ <i class="req">*</i></label>
          <select id="cod-write-off-advance" class="fld-input" v-model="writeOff.cod_advance_request_id"
                  :disabled="advancesLoading || !openAdvances.length" @change="onAdvancePicked">
            <option :value="null" disabled>
              {{ advancesLoading ? 'ກຳລັງໂຫລດ...' : (openAdvances.length ? 'ເລືອກລາຍການຖອນ' : 'ບໍ່ມີລາຍການທີ່ຄ້າງຊຳລະ') }}
            </option>
            <option v-for="a in openAdvances" :key="a.id" :value="a.id">
              {{ advanceLabel(a) }}
            </option>
          </select>
          <p class="fld-hint is-bad" v-if="advancesError">{{ advancesError }}</p>
          <p class="fld-hint" v-else-if="pickedAdvance">
            ຖອນ {{ formatKip(pickedAdvance.amount) }} · ໂອນເມື່ອ {{ formatDateTime(pickedAdvance.disbursed_at) }}
          </p>
        </div>

        <div class="fld">
          <label class="fld-label">ຈຳນວນເງິນ <i class="req">*</i></label>
          <KipInput v-model="writeOff.amount" :max="maxWriteOff" :disabled="!pickedAdvance"/>
          <p class="fld-hint" :class="{'is-bad': amountTooBig}" v-if="pickedAdvance">
            {{ amountTooBig
              ? 'ຫຼາຍກວ່າຍອດຄ້າງຊຳລະຂອງລາຍການນີ້ ' + formatKip(maxWriteOff)
              : 'ສູງສຸດ ' + formatKip(maxWriteOff) }}
          </p>
        </div>

        <div class="fld">
          <label class="fld-label">ເຫດຜົນ <i class="req">*</i></label>
          <textarea class="fld-input" rows="3" v-model="writeOff.reason" maxlength="500"
                    placeholder="ເຫດຜົນຈະຖືກບັນທຶກໄວ້ຖາວອນ"></textarea>
          <p class="fld-hint counter">{{ writeOff.reason.length }} / 500</p>
        </div>

        <p class="form-error" v-if="writeOffError">{{ writeOffError }}</p>

        <div class="modal-actions">
          <button class="cod-btn is-ghost" @click="writeOffActive = false">ຍົກເລີກ</button>
          <button class="cod-btn is-danger" :disabled="writeOffSaving || !canSubmitWriteOff"
                  @click="submitWriteOff">
            {{ writeOffSaving ? 'ກຳລັງດຳເນີນການ...' : 'ຢືນຢັນຕັດໜີ້' }}
          </button>
        </div>
      </div>
    </vs-dialog>
  </div>
</template>

<script>
import Pagination from '@/Plugins/Paginations/Pagination';
import KipInput from '@/views/Accounting/Customer/CodAdvance/components/KipInput';
import RepaymentModal from '@/views/Accounting/Customer/CodAdvance/components/RepaymentModal';
import {
  downloadAuditExport,
  errorMessage,
  fetchAudit,
  fetchOpenAdvances,
  formatDateTime,
  formatKip,
  formatPct,
  writeOffAdvance,
} from '@/services/codAdvanceService';

export default {
  name: 'AuditTab',
  components: {Pagination, KipInput, RepaymentModal},
  data() {
    return {
      rows: [],
      sumOutstanding: 0,
      filters: {search: '', shop_status: 'all'},
      pagination: {current_page: 1, last_page: 1, per_page: 10, total: 0},
      error: null,
      isLoading: false,
      exporting: false,
      writeOffActive: false,
      writeOffRow: null,
      writeOff: {cod_advance_request_id: null, amount: null, reason: ''},
      writeOffError: null,
      writeOffSaving: false,
      writeOffOpenedAt: null,
      openAdvances: [],
      advancesLoading: false,
      advancesError: null,
      repaymentActive: false,
      repaymentRow: null,
      repaymentBusy: false,
    };
  },
  computed: {
    canWriteOff() {
      return this.$can(['SuperAdmin', 'accountingcod']);
    },
    pickedAdvance() {
      return this.openAdvances.find(a => a.id === this.writeOff.cod_advance_request_id) || null;
    },
    // A write-off is bounded by the PICKED advance's remainder (the ledger
    // refuses more), not by the shop's whole debt.
    maxWriteOff() {
      return this.pickedAdvance ? Number(this.pickedAdvance.outstanding) || 0 : 0;
    },
    amountTooBig() {
      return !!(this.pickedAdvance && this.writeOff.amount > this.maxWriteOff);
    },
    canSubmitWriteOff() {
      return !!this.pickedAdvance
        && this.writeOff.amount > 0
        && !this.amountTooBig
        && !!this.writeOff.reason.trim();
    },
  },
  watch: {
    // However the repayment dialog closes, the list is reloaded: a save may
    // have landed even if its answer never reached the (destroyed) form.
    repaymentActive(open) {
      if (!open) {
        this.repaymentBusy = false;
        this.fetchData(this.pagination.current_page);
      }
    },
  },
  created() {
    this.fetchData(1);
  },
  methods: {
    formatDateTime,
    formatKip,
    formatPct,
    rowNumber(index) {
      return (this.pagination.current_page - 1) * this.pagination.per_page + index + 1;
    },
    initialOf(name) {
      return name ? String(name).trim().charAt(0).toUpperCase() : '?';
    },
    async fetchData(page) {
      this.error = null;
      this.isLoading = true;
      try {
        const data = await fetchAudit(this.params(page));
        this.rows = data.data || [];
        this.sumOutstanding = data.sum_outstanding || 0;
        this.pagination = {
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
        };
      } catch (e) {
        this.error = errorMessage(e, 'ໂຫລດຂໍ້ມູນບໍ່ສຳເລັດ');
      } finally {
        this.isLoading = false;
      }
    },
    params(page) {
      return {
        search: this.filters.search || undefined,
        shop_status: this.filters.shop_status,
        page: page || this.pagination.current_page,
      };
    },
    async exportRows() {
      if (this.exporting) return;
      this.exporting = true;
      try {
        const response = await downloadAuditExport(this.params(1));
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'COD Advance Audit.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.error = errorMessage(e, 'Export ບໍ່ສຳເລັດ');
      } finally {
        this.exporting = false;
      }
    },
    withdrawDate(row) {
      return row.oldest_open_advance_requested_at || row.oldest_open_advance_at || null;
    },
    advanceLabel(a) {
      return (a.invoice_number || '#' + a.id)
        + ' · ' + formatDateTime(a.requested_at)
        + ' · ຄ້າງ ' + formatKip(a.outstanding);
    },
    openRepayment(row) {
      this.repaymentRow = row;
      this.repaymentActive = true;
    },
    afterRepayment() {
      this.repaymentActive = false; // the watcher reloads the list
    },
    openWriteOff(row) {
      this.writeOffRow = row;
      const openedAt = Date.now();
      this.writeOffOpenedAt = openedAt;
      this.writeOff = {cod_advance_request_id: null, amount: null, reason: ''};
      this.writeOffError = null;
      this.writeOffActive = true;
      this.loadOpenAdvances(row.customer.id, openedAt);
    },
    async loadOpenAdvances(customerId, openedAt) {
      this.openAdvances = [];
      this.advancesError = null;
      this.advancesLoading = true;
      try {
        const advances = await fetchOpenAdvances(customerId);
        // A slow answer for a modal already reopened on another shop is dropped.
        if (openedAt !== this.writeOffOpenedAt) return;
        this.openAdvances = advances;
        if (advances.length === 1) {
          this.writeOff.cod_advance_request_id = advances[0].id;
          this.onAdvancePicked();
        }
      } catch (e) {
        if (openedAt === this.writeOffOpenedAt) {
          this.advancesError = errorMessage(e, 'ໂຫລດລາຍການຖອນບໍ່ສຳເລັດ');
        }
      } finally {
        if (openedAt === this.writeOffOpenedAt) {
          this.advancesLoading = false;
        }
      }
    },
    onAdvancePicked() {
      this.writeOff.amount = this.pickedAdvance ? this.maxWriteOff : null;
    },
    // The idempotency key is derived from the PAYLOAD, not minted once per
    // modal. CodAdvanceLedgerService::writeOff returns the existing row for a
    // known request_id BEFORE re-validating, so a fixed key would make a
    // corrected amount report success while the original amount stands. Same
    // payload = same key (a lost response retries safely); changed payload =
    // new key (a correction is a new attempt, not a retry).
    writeOffRequestId() {
      const payload = [
        this.writeOff.cod_advance_request_id,
        this.writeOff.amount,
        this.writeOff.reason.trim(),
      ].join('|');
      let hash = 0;
      for (let i = 0; i < payload.length; i += 1) {
        hash = ((hash << 5) - hash + payload.charCodeAt(i)) | 0;
      }
      return 'WO-' + this.writeOffRow.customer.id + '-' + this.writeOffOpenedAt
        + '-' + (hash >>> 0).toString(36);
    },
    async submitWriteOff() {
      if (this.writeOffSaving) return;
      this.writeOffError = null;
      if (!this.canSubmitWriteOff) {
        this.writeOffError = 'ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບ';
        return;
      }
      this.writeOffSaving = true;
      try {
        await writeOffAdvance(this.writeOffRow.customer.id, {
          request_id: this.writeOffRequestId(),
          cod_advance_request_id: this.writeOff.cod_advance_request_id,
          amount: this.writeOff.amount,
          reason: this.writeOff.reason.trim(),
        });
        this.writeOffActive = false;
        this.fetchData(this.pagination.current_page);
      } catch (e) {
        this.writeOffError = errorMessage(e, 'ຕັດໜີ້ບໍ່ສຳເລັດ');
      } finally {
        this.writeOffSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './cod-advance-ui';

.dialog-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  text-align: center;
  width: 100%;
}

.write-off {
  text-align: left;
  width: 440px;
  max-width: 100%;
}

.who {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid $cod-line;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 14px;
  background: #fafbfc;
}

.who-owing {
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  span {
    font-size: 11px;
    color: $cod-muted;
  }

  b {
    color: $cod-danger;
  }
}

.danger-note {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #fdecee;
  border: 1px solid #f8d4d8;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  color: $cod-danger;
  margin-bottom: 16px;
}

.fld-hint.is-bad {
  color: $cod-danger;
}

.row-actions {
  display: flex;
  gap: 6px;
}

.counter {
  text-align: right;
  margin: 6px 0 0;
}
</style>
